/**
 * Erkennung echter Datei-Drags (von außerhalb des Browsers) für die window-weiten
 * Drag&Drop-Handler in `App.vue` und die Dropzone der `UploadCard`.
 *
 * `dataTransfer.types.includes('Files')` allein genügt NICHT: Chrome/Firefox melden auch ein
 * innerhalb der Seite gezogenes `<img>` (z. B. OSMDs absolut positionierte Cursor-Bilder in der
 * Notenvorschau) als `"Files"`. Ein Klick-Halten-Ziehen über der Partitur öffnete so das
 * Upload-Overlay und konnte beim Loslassen sogar einen Fehl-Upload auslösen.
 *
 * Tragende Unterscheidung: `dragstart` feuert nur für Drags, die IM Dokument beginnen – eine von
 * außen hereingezogene Datei feuert nie `dragstart` in der Seite. `createWindowFileDragState`
 * merkt sich deshalb laufende In-Page-Drags und `isExternalFileDrag` verneint für sie.
 */

/** Roher `Files`-Check – nur als Baustein bzw. für lokale Dropzones (UploadCard) gedacht. */
export function dragHasFiles(event: DragEvent): boolean {
  return event.dataTransfer?.types?.includes('Files') ?? false;
}

export interface WindowFileDragState {
  /** window-`dragstart` (Bubble-Phase): merkt einen In-Page-Drag vor. */
  onDragStart(event: DragEvent): void;
  /** window-`dragend`, Drop (Capture) und `pointerdown`: In-Page-Merker zurücksetzen. */
  reset(): void;
  /** true nur für einen Datei-Drag, der NICHT in der Seite begonnen hat. */
  isExternalFileDrag(event: DragEvent): boolean;
}

export function createWindowFileDragState(): WindowFileDragState {
  let internalDrag = false;
  return {
    onDragStart(event: DragEvent): void {
      // Ein per preventDefault() abgebrochener dragstart (Score-Container, siehe
      // MusicXmlPreview.vue) startet den Drag nie – es folgt also auch kein dragend, der Merker
      // bliebe fälschlich hängen und der nächste ECHTE Datei-Drag würde ignoriert.
      if (!event.defaultPrevented) internalDrag = true;
    },
    reset(): void {
      // Zusätzlich an window-`pointerdown` gebunden (Beginn jeder neuen Mausgeste) als
      // Sicherheitsnetz gegen jeden denkbar hängengebliebenen Merker: eine von außen gezogene
      // Datei feuert nie `pointerdown`, das Zurücksetzen ist also immer gefahrlos.
      internalDrag = false;
    },
    isExternalFileDrag(event: DragEvent): boolean {
      return !internalDrag && dragHasFiles(event);
    },
  };
}
